import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Placeholder from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import LinkIcon from "@mui/icons-material/Link";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import TableChartIcon from "@mui/icons-material/TableChart";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import TitleIcon from "@mui/icons-material/Title";
import TextFieldsIcon from "@mui/icons-material/TextFields";

const TEXT_COLORS = [
  { label: "Default", value: "default" },
  { label: "Black", value: "#000000" },
  { label: "Gray", value: "#6b7280" },
  { label: "Red", value: "#dc2626" },
  { label: "Orange", value: "#ea580c" },
  { label: "Green", value: "#16a34a" },
  { label: "Blue", value: "#2563eb" },
  { label: "Purple", value: "#7c3aed" },
];

const ToolbarButton = ({ title, active, disabled, onClick, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        color={active ? "primary" : "default"}
        disabled={disabled}
        onClick={onClick}
        sx={{
          borderRadius: 1,
          bgcolor: active ? "action.selected" : "transparent",
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const TipTapEditorComponent = ({
  error = null,
  initialValue,
  handleBlur,
  handleChange = null,
  onReady,
  disablePlugins = [],
  tokens = [],
  isDisabled = false,
}) => {
  const [selectKey, setSelectKey] = useState(0);
  const showTable = !disablePlugins.includes("table");
  const readyNotifiedRef = useRef(false);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextStyle,
      Color,
      Placeholder.configure({
        placeholder: "Start typing...",
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      ...(showTable
        ? [
            Table.configure({ resizable: false }),
            TableRow,
            TableHeader,
            TableCell,
          ]
        : []),
    ],
    [showTable],
  );

  const editor = useEditor({
    extensions,
    content: initialValue || "",
    editable: !isDisabled,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: `tiptap-content min-h-[180px] px-3 py-2`,
        style: "min-height: 180px; padding: 8px 12px; outline: none;",
      },
    },
    onCreate: ({ editor: ed }) => {
      readyNotifiedRef.current = true;
      onReady?.(ed.getHTML());
    },
    onUpdate: ({ editor: ed, transaction }) => {
      if (!transaction.docChanged) return;
      handleChange?.(ed.getHTML());
    },
    onBlur: ({ editor: ed }) => {
      const html = ed.getHTML();
      const normalized = html.replace(/\s+/g, " ").trim();
      const isEffectivelyEmpty =
        !normalized ||
        normalized === "<p></p>" ||
        normalized === "<p><br></p>" ||
        normalized === "<p><br/></p>";
      if (
        isEffectivelyEmpty &&
        (initialValue || "").replace(/\s+/g, " ").trim().length > 40
      ) {
        return;
      }
      handleBlur(html);
    },
  });

  useEffect(() => {
    readyNotifiedRef.current = false;
  }, [initialValue]);

  useEffect(() => {
    if (!editor || readyNotifiedRef.current) return;
    readyNotifiedRef.current = true;
    onReady?.(editor.getHTML());
  }, [editor, onReady]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!isDisabled);
  }, [editor, isDisabled]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = initialValue || "";
    if (next !== current && next !== "<p></p>") {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, initialValue]);

  const setLink = useCallback(() => {
    if (!editor || isDisabled) return;
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL", previous || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor, isDisabled]);

  const insertToken = useCallback(
    (tokenValue) => {
      if (!editor || isDisabled || !tokenValue) return;
      editor
        .chain()
        .focus()
        .insertContent(`<span contenteditable="false">{{${tokenValue}}}</span>&nbsp;`)
        .run();
      handleBlur(editor.getHTML());
      setSelectKey((prev) => prev + 1);
    },
    [editor, isDisabled, handleBlur],
  );

  if (!editor) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <div className={`tiptap-editor-shell${error ? " has-error" : ""}`}>
        <div className="tiptap-editor-toolbar">
          <ToolbarButton
            title="Undo"
            disabled={isDisabled || !editor.can().undo()}
            onClick={() => editor.chain().focus().undo().run()}
          >
            <UndoIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Redo"
            disabled={isDisabled || !editor.can().redo()}
            onClick={() => editor.chain().focus().redo().run()}
          >
            <RedoIcon fontSize="small" />
          </ToolbarButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolbarButton
            title="Paragraph"
            active={editor.isActive("paragraph")}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().setParagraph().run()}
          >
            <TextFieldsIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Heading 1"
            active={editor.isActive("heading", { level: 1 })}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          >
            <TitleIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Heading 2"
            active={editor.isActive("heading", { level: 2 })}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <TitleIcon fontSize="small" sx={{ fontSize: 18 }} />
          </ToolbarButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolbarButton
            title="Bold"
            active={editor.isActive("bold")}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBoldIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Italic"
            active={editor.isActive("italic")}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalicIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Underline"
            active={editor.isActive("underline")}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Strikethrough"
            active={editor.isActive("strike")}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <StrikethroughSIcon fontSize="small" />
          </ToolbarButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <FormControl size="small" sx={{ minWidth: 110, mx: 0.5 }}>
            <Select
              displayEmpty
              disabled={isDisabled}
              value=""
              onChange={(e) => {
                const val = e.target.value;
                if (!val) return;
                if (val === "default") {
                  editor.chain().focus().unsetColor().run();
                } else {
                  editor.chain().focus().setColor(val).run();
                }
              }}
              renderValue={() => "Color"}
              sx={{ height: 32, fontSize: 13 }}
            >
              {TEXT_COLORS.map((c) => (
                <MenuItem key={c.label} value={c.value}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        border: "1px solid #ccc",
                        bgcolor: c.value === "default" ? "text.primary" : c.value,
                      }}
                    />
                    {c.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolbarButton
            title="Align left"
            active={editor.isActive({ textAlign: "left" })}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            <FormatAlignLeftIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Align center"
            active={editor.isActive({ textAlign: "center" })}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            <FormatAlignCenterIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Align right"
            active={editor.isActive({ textAlign: "right" })}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            <FormatAlignRightIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Justify"
            active={editor.isActive({ textAlign: "justify" })}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          >
            <FormatAlignJustifyIcon fontSize="small" />
          </ToolbarButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolbarButton
            title="Bullet list"
            active={editor.isActive("bulletList")}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FormatListBulletedIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Numbered list"
            active={editor.isActive("orderedList")}
            disabled={isDisabled}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <FormatListNumberedIcon fontSize="small" />
          </ToolbarButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolbarButton
            title="Link"
            active={editor.isActive("link")}
            disabled={isDisabled}
            onClick={setLink}
          >
            <LinkIcon fontSize="small" />
          </ToolbarButton>

          {showTable && (
            <ToolbarButton
              title="Insert table"
              active={editor.isActive("table")}
              disabled={isDisabled}
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                  .run()
              }
            >
              <TableChartIcon fontSize="small" />
            </ToolbarButton>
          )}

          <ToolbarButton
            title="Clear formatting"
            disabled={isDisabled}
            onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          >
            <FormatClearIcon fontSize="small" />
          </ToolbarButton>
        </div>

        <EditorContent editor={editor} />
      </div>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      {Array.isArray(tokens) && tokens.length > 0 && (
        <FormControl fullWidth sx={{ mt: 2, width: "50%" }} disabled={isDisabled}>
          <InputLabel id="mergeTagsList">Tags</InputLabel>
          <Select
            key={selectKey}
            labelId="mergeTagsList"
            id="mergeTagsList"
            value=""
            label="Tags"
            onChange={(e) => insertToken(e.target.value)}
          >
            {tokens.map((tag, idx) => (
              <MenuItem key={idx} value={tag.value}>
                {tag.label}
              </MenuItem>
            ))}
          </Select>
          {tokens.length === 0 && <FormHelperText>No merge tags available</FormHelperText>}
        </FormControl>
      )}
    </Box>
  );
};

export default TipTapEditorComponent;
